<template>
  <div
    class="containerSlideEditor"
    :ref="'containerSlideEditor'"
    :class="classesTemplate"
  >
    <div
      class="coloredBackground"
      :class="classesTemplate"
      :style="'border-bottom: 4px solid ' + theme + '; background-color:' + slideBackgroundColor"
    >
      <!-- VIDEO BACKGROUND -->
      <VideoImageGenerator
        v-if="isVideoBackground && !proceedImage"
        :url="slideVideoImageUrl || slideImageUrl"
        :forceImagePreview="lastImageUrlGenerated"
        :orientation="'landscape'"
        :mask="maskForImage"
        :stopAnimationWhenNotPlaying="true"
        @imageGenerated="onImageGenerated"
        @videoGenerated="onVideoGenerated"
        :prompt="currentSlide?.promptIllustrationGeneration"
      />

      <!-- TITLE -->
      <div class="titleElement" :class="classesTemplate">
        <div
          v-html="slideTitle"
          ref="title-container"
          :contenteditable="isEditable"
          :placeholder="$t('aiAvatar.slideTemplate.titlePlaceholder')"
          class="titleTxt"
          :style="'color:' + theme + '; font-size:' + titleFontSize + 'px;'"
          @focus="onFieldFocus('title')"
          @focusout="onFieldBlur($event, 'title')"
        />
      </div>

      <!-- DESCRIPTION -->
      <div class="descriptionElement" :class="classesTemplate">
        <!-- Anchor hugs the text so the toolbar can sit 8px above its top in CSS. -->
        <div class="descriptionAnchor">
          <!-- FORMATTING TOOLBAR (just above the description) -->
          <SlideTextToolbar
            v-if="isEditable && descriptionFocused"
            class="slideToolbarFloating forDescription"
            :last-color="lastColor"
            :state="formatState"
            @command="applyCommand"
            @color-picker-active="onColorPickerActive"
          />
          <div
            v-html="slideDescription"
            ref="description-container"
            :style="
              'border-left: 1px solid ' +
              theme +
              '; font-size:' +
              descriptionFontSize +
              'px;'
            "
            :placeholder="$t('aiAvatar.slideTemplate.descriptionPlaceholder')"
            :contenteditable="isEditable"
            class="descriptionTxt"
            @focus="onFieldFocus('description')"
            @focusout="onFieldBlur($event, 'description')"
            @mouseup="saveSelection"
            @keyup="saveSelection"
          />
        </div>
      </div>
    </div>

    <div class="avatarLayout" :class="classesTemplate">
      <div
        class="avatarBackground"
        :style="'background-image:url(' + backgroundSelected?.url + ')'"
      ></div>
      <VideoAvatarPreview />
    </div>

    <div class="videoLayout" :class="classesTemplate">
      <VideoImageGenerator
        v-if="isVideoLayoutBackground && !proceedImage"
        :url="slideVideoImageUrl || slideImageUrl"
        :forceImagePreview="lastImageUrlGenerated"
        :orientation="'portrait'"
        :stopAnimationWhenNotPlaying="true"
        @imageGenerated="onImageGenerated"
        @videoGenerated="onVideoGenerated"
        :mask="'none'"
        :prompt="currentSlide?.promptIllustrationGeneration"
      />
    </div>
    <div class="logoElement" :class="classesTemplate">
      <img class="logoWorkspace" :src="logoSlide" />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import store from "@/store";
import {
  AvatarStyle,
  LIST_OF_BACKGROUND,
} from "@/store/AIAvatar/aiavatarGenerationModel";
import { getImageURLForSize, htmliFy, SLIDE_RICHTEXT_TAGS } from "@/utils/util";
import { txtColorForBackground } from "@/utils/color";
import { clone } from "@/utils/cloneUtil";
import { Slide, SlideTemplate } from "@/store/slides2Video/slides2VideoModels";
import { registerSlideEditingFlush, updateCurrentSlide } from "@/utils/slides/slideUtil";
import VideoImageBackground from "@/components/videoImageBackground/VideoImageBackground.vue";
import VideoImageGenerator from "@/components/videoImageBackground/VideoImageGenerator.vue";
import {
  DESCRIPTION_FONT_RATIO,
  TITLE_FONT_RATIO,
  SlideTextCommand,
} from "@/components/recorder/aiAvatar/panel/AISlideConstant";
import { isSpeach } from "@/utils/speachUtils";
import VideoAvatarPreview from "@/components/videoImageBackground/VideoAvatarPreview.vue";
import SlideTextToolbar from "@/components/recorder/aiAvatar/panel/SlideTextToolbar.vue";
import {
  colorSelection,
  currentColor,
  formatSelection,
  isCommandActive,
} from "@/utils/slides/richTextEditor";

@Component({
  components: {
    SlideTextToolbar,
    VideoAvatarPreview,
    VideoImageGenerator,
    VideoImageBackground,
  },
})
export default class AISlidePanel extends Vue {
  @Prop({ default: false })
  private shouldEditable!: boolean;

  private slideContainerWidth: number = 0;
  private resizeObserverSlideContainer!: ResizeObserver;

  private isEditable = false;

  private proceedImage = false;

  private lastImageUrlGenerated = "";

  // Rich-text editing state
  private descriptionFocused = false;
  private savedRange: Range | null = null;
  private lastColor = "#000000";
  private formatState: Record<string, boolean> = {
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    insertUnorderedList: false,
    insertOrderedList: false,
  };
  private selectionChangeHandler: (() => void) | null = null;
  // True while the color picker is open.
  private colorPickerSession = false;
  // Color chosen in the picker, applied to the saved selection when it closes.
  private pendingColor: string | null = null;
  // Snapshot of the caret/selection taken when the picker opens.
  private colorOpenRange: Range | null = null;

  get isVideoBackground() {
    return (
      this.slideTemplate === SlideTemplate.TITLE_ILLUSTRATED ||
      this.slideTemplate === SlideTemplate.ILLUSTRATED_ONLY
    );
  }
  get maskForImage() {
    return this.slideTemplate === SlideTemplate.TITLE_ILLUSTRATED
      ? "light"
      : "";
  }
  get isVideoLayoutBackground() {
    return (
      this.slideTemplate === SlideTemplate.ILLUSTRATED_RIGHT ||
      this.slideTemplate === SlideTemplate.ILLUSTRATED_LEFT
    );
  }

  get classesTemplate() {
    return this.slideTemplate;
  }

  get slideTitle() {
    const html = htmliFy(this.currentSlide?.slideTitle || "", SLIDE_RICHTEXT_TAGS);
    // Render truly empty (no stray <br>/whitespace) so the :empty placeholder shows.
    return this.isBlankContent(html) ? "" : html;
  }

  get slideDescription() {
    const html = htmliFy(
      this.currentSlide?.slideDescription || "",
      SLIDE_RICHTEXT_TAGS
    );
    return this.isBlankContent(html) ? "" : html;
  }

  // A field that only contains stray <br>/whitespace must be treated as empty,
  // otherwise the contenteditable is no longer :empty and the placeholder is lost.
  private isBlankContent(html: string): boolean {
    return (
      html
        .replace(/<br\s*\/?>/gi, "")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/[\u200B\uFEFF]/g, "")
        .trim() === ""
    );
  }

  get slideTemplate() {
    return this.currentSlide?.slideTemplate || SlideTemplate.AVATAR_LEFT;
  }

  get theme() {
    return this.currentSlide?.slideTheme || "#7957D5";
  }

  get slideBackgroundColor() {
    return this.currentSlide?.slideBackgroundColor || "#FFFFFF";
  }

  get slideImageUrl() {
    if (this.currentSlide?.slideImageB64) {
      return `${this.currentSlide.slideImageB64}`;
    } else {
      return "";
    }
  }
  get slideVideoImageUrl() {
    if (this.currentSlide?.slideVideoUrl) {
      return `${this.currentSlide.slideVideoUrl}`;
    } else {
      return "";
    }
  }

  get txtColor() {
    return txtColorForBackground(this.theme);
  }

  get avatarSelected() {
    return store.getters.getAvatarSelected;
  }

  get backgroundSelected() {
    return LIST_OF_BACKGROUND.find((b) => {
      return store.getters.getAvatarBabckgroundSelected === b.background;
    });
  }

  get currentSlide(): Slide | null {
    if (store.getters.getCurrentSlideIndex > -1) {
      return store.getters.slides[store.getters.getCurrentSlideIndex];
    }
    return null;
  }

  get titleFontSize() {
    return this.slideContainerWidth / TITLE_FONT_RATIO;
  }
  get descriptionFontSize() {
    return this.slideContainerWidth / DESCRIPTION_FONT_RATIO;
  }

  get logoSlide() {
    if (isSpeach()) {
      return store.getters.getSpeachLogo;
    } else {
      return getImageURLForSize(
        store.getters.getSelectedWorkspace?.imageURL,
        160
      );
    }
  }

  mounted() {
    this.isEditable = this.shouldEditable;

    this.initObserver();

    // Let anything that needs to know what the user typed force a save (leave guards, close...)
    registerSlideEditingFlush(() => this.flushFields());

    // Keep the toolbar button states in sync with the caret as it moves.
    this.selectionChangeHandler = () => this.onSelectionChange();
    document.addEventListener("selectionchange", this.selectionChangeHandler);
  }

  private initObserver() {
    const el = this.$refs["containerSlideEditor"] as HTMLDivElement;
    // Nettoyage observer
    if (this.resizeObserverSlideContainer) {
      this.resizeObserverSlideContainer.disconnect();
    }
    this.resizeObserverSlideContainer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.slideContainerWidth = entry.contentRect.width;
      }
    });
    this.resizeObserverSlideContainer.observe(el);
  }

  private onFieldFocus(field: "title" | "description") {
    this.descriptionFocused = field === "description";
  }

  private onFieldBlur(event: FocusEvent, field: "title" | "description") {
    const element = event.target as HTMLElement | null;
    setTimeout(() => {
      if (this.colorPickerSession) {
        return;
      }
      const active = document.activeElement as HTMLElement | null;
      if (active === element) {
        return;
      }
      const container = this.$refs["containerSlideEditor"] as HTMLElement;
      const inToolbar = !!active && !!active.closest(".slideTextToolbar");
      const inContainer = !!active && !!container && container.contains(active);

      if (inToolbar) {
        return;
      }
      // Field is really being left: persist its content now.
      if (element) {
        this.saveField(field, element);
      }
      if (!inContainer) {
        this.descriptionFocused = false;
      }
    }, 0);
  }

  private onColorPickerActive(active: boolean) {
    if (active) {
      // Picker opened: protect the field from picker-induced blurs until close
      this.colorPickerSession = true;
      this.pendingColor = null;
      this.colorOpenRange = this.savedRange ? this.savedRange.cloneRange() : null;
    } else {
      this.restoreColorCaretAndApplyColor();
    }
  }

  // When the color picker closes, put the focus and caret back on the field
  private restoreColorCaretAndApplyColor() {
    const editable = this.activeEditable();
    // Use the caret snapshot from when the picker opened, NOT savedRange
    const range = this.colorOpenRange;
    const color = this.pendingColor;
    if (!editable || !range) {
      this.colorPickerSession = false;
      this.pendingColor = null;
      this.colorOpenRange = null;
      return;
    }
    // Defer so this runs AFTER the closing click's own focus change — otherwise
    // the browser moves focus to whatever was clicked and overrides us
    setTimeout(() => {
      editable.focus();
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      if (color) {
        // Now that the editable is focused with the original selection restored, applying the color is reliable
        colorSelection(color);
        const applied = window.getSelection();
        if (applied && applied.rangeCount > 0) {
          this.savedRange = applied.getRangeAt(0).cloneRange();
        }
      }
      this.colorPickerSession = false;
      this.pendingColor = null;
      this.colorOpenRange = null;
    }, 0);
  }

  // Remember the current selection so it can be restored after interacting with the color picker popup (which steals focus from the contenteditable).
  private saveSelection(event: Event) {
    const editable = event.currentTarget as HTMLElement;
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editable.contains(range.commonAncestorContainer)) {
        this.savedRange = range.cloneRange();
      }
    }
  }

  // The toolbar only ever acts on the description (the title isn't formattable).
  private activeEditable(): HTMLElement | null {
    return this.descriptionFocused
      ? (this.$refs["description-container"] as HTMLElement)
      : null;
  }

  private applyCommand(command: SlideTextCommand) {
    const editable = this.activeEditable();
    if (!editable) {
      return;
    }

    if (command.cmd === "foreColor" && command.value) {
      this.pendingColor = command.value;
      this.lastColor = command.value;
      return;
    } else if (
      command.cmd === "insertUnorderedList" ||
      command.cmd === "insertOrderedList"
    ) {
      // Lists restructure the DOM and reset the caret, so preserve its position.
      const offset = this.getCaretOffset(editable);
      formatSelection(command.cmd);
      if (offset !== null) {
        editable.focus();
        this.setCaretOffset(editable, offset);
      }
    } else {
      // bold / italic / underline / strikethrough
      formatSelection(command.cmd);
    }

    this.updateFormatState();
  }

  private onSelectionChange() {
    const editable = this.activeEditable();
    if (!editable || document.activeElement !== editable) {
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    if (!editable.contains(range.commonAncestorContainer)) {
      return;
    }
    // Remember the exact caret/selection so the color picker can restore it.
    this.savedRange = range.cloneRange();
    this.updateFormatState();
    this.updateCurrentColor();
  }

  private updateFormatState() {
    const editable = this.activeEditable();
    if (!editable) {
      return;
    }
    this.formatState = {
      bold: isCommandActive("bold"),
      italic: isCommandActive("italic"),
      underline: isCommandActive("underline"),
      strikeThrough: isCommandActive("strikeThrough"),
      insertUnorderedList: isCommandActive("insertUnorderedList"),
      insertOrderedList: isCommandActive("insertOrderedList"),
    };
  }

  // Reflect the text color at the caret in the toolbar swatch.
  private updateCurrentColor() {
    const editable = this.activeEditable();
    if (!editable) {
      return;
    }
    const hex = currentColor();
    if (hex) {
      this.lastColor = hex;
    }
  }

  // Character offset of the caret within an editable (ignoring HTML structure).
  private getCaretOffset(root: HTMLElement): number | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }
    const range = selection.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(root);
    preRange.setEnd(range.endContainer, range.endOffset);
    return preRange.toString().length;
  }

  private setCaretOffset(root: HTMLElement, offset: number) {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let remaining = offset;
    let target: Text | null = null;
    let targetOffset = 0;
    let node = walker.nextNode();
    while (node) {
      const len = (node.textContent || "").length;
      if (remaining <= len) {
        target = node as Text;
        targetOffset = remaining;
        break;
      }
      remaining -= len;
      node = walker.nextNode();
    }
    const range = document.createRange();
    if (target) {
      range.setStart(target, targetOffset);
    } else {
      range.selectNodeContents(root);
    }
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  // Push whatever is currently in the two contenteditable into the store, right now.
  // Called before leaving the editor (and by the leave guards), because saveField()
  // otherwise only runs on focusout, through a setTimeout that may never get its turn.
  private flushFields() {
    const title = this.$refs["title-container"] as HTMLElement | undefined;
    if (title) {
      this.saveField("title", title);
    }
    const description = this.$refs["description-container"] as HTMLElement | undefined;
    if (description) {
      this.saveField("description", description);
    }
  }

  private saveField(field: "title" | "description", element: HTMLElement) {
    const newSlide = clone(this.currentSlide) as Slide | null;
    if (!newSlide) {
      return;
    }
    let sanitized = htmliFy(element.innerHTML.trim(), SLIDE_RICHTEXT_TAGS);
    if (this.isBlankContent(sanitized)) {
      sanitized = "";
    }
    if (field === "title") {
      if (sanitized === this.slideTitle.trim()) {
        return;
      }
      newSlide.slideTitle = sanitized;
    } else {
      if (sanitized === this.slideDescription.trim()) {
        return;
      }
      newSlide.slideDescription = sanitized;
    }
    updateCurrentSlide(newSlide);
  }

  private async onImageGenerated({
    prompt,
    image,
  }: {
    prompt: string;
    image: string;
  }) {
    const newSlide = clone(this.currentSlide) as Slide;
    newSlide.slideImageB64 = image;
    newSlide.promptIllustrationGeneration = prompt;
    // An image replaces any previous video, otherwise the video keeps winning in the url fallback
    newSlide.slideVideoUrl = "";
    this.lastImageUrlGenerated = "";
    updateCurrentSlide(newSlide);
    this.proceedImage = true;
    setTimeout(() => {
      this.proceedImage = false;
    }, 500);
  }
  private async onVideoGenerated({
    prompt,
    video,
    image,
  }: {
    prompt: string;
    video: string;
    image: string;
  }) {
    const newSlide = clone(this.currentSlide) as Slide;
    newSlide.promptIllustrationGeneration = prompt;
    newSlide.slideVideoUrl = video;
    this.lastImageUrlGenerated = image;
    updateCurrentSlide(newSlide);
    this.proceedImage = true;
    setTimeout(() => {
      this.proceedImage = false;
    }, 500);
  }

  @Watch("slideTemplate")
  async onSlideTemplateChange() {
    this.initObserver();
    if (this.currentSlide) {
      const newSlide = clone(this.currentSlide) as Slide;
      updateCurrentSlide(newSlide);
    }
  }

  beforeDestroy() {
    // the panel is being dropped: don't lose what was typed and not yet saved
    this.flushFields();
    registerSlideEditingFlush(null);
    // Nettoyage observer
    if (this.resizeObserverSlideContainer) {
      this.resizeObserverSlideContainer.disconnect();
    }
    if (this.selectionChangeHandler) {
      document.removeEventListener(
        "selectionchange",
        this.selectionChangeHandler
      );
    }
  }
}
</script>

<style lang="scss" scoped>
// Formatting toolbar, floated 8px above the top of the description text
.slideToolbarFloating {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  bottom: calc(100% + 16px);
}

.containerSlideEditor {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
  color: white;

  .coloredBackground {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    background: white;
    flex-direction: column;
    box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.4);
  }

  .logoElement {
    position: absolute;
    bottom: 12px;
    right: 16px;
    width: 120px;
    height: 32px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    .logoWorkspace {
      height: 100%;
      object-fit: contain;
      margin-left: auto;
    }
  }
  .titleElement {
    position: relative;
    font-weight: 700;
    max-height: 33%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 4%;
    margin-bottom: 4%;
    .titleTxt {
      margin-left: 4%;
      margin-right: 4%;
      padding-left: 4%;
      padding-right: 4%;
      font-weight: 900;
      cursor: text;
      &[placeholder]:empty::before {
        content: attr(placeholder);
        color: var(--light1);
      }
    }
  }
  .descriptionElement {
    position: relative;
    margin-top: 0;
    margin-bottom: 4%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    max-height: 67%;
    // Inset the text area from the slide edges
    padding-left: 4%;
    padding-right: 4%;
    color: var(--black);
    .descriptionAnchor {
      position: relative;
      width: max-content;
      max-width: 100%;
    }
    .descriptionTxt {
      // em paddings (not %) so they're counted in the max-content width in descriptionAnchor above
      padding-left: 0.5em;
      padding-right: 0.5em;
      text-align: left;
      overflow-wrap: break-word;
      cursor: text;
      font-weight: normal;
      ::v-deep strong,
      ::v-deep b {
        color: inherit;
        font-weight: 900;
      }
      ::v-deep pre {
        background: transparent;
        font-family: inherit;
        font-size: inherit;
      }
      ::v-deep u {
        text-decoration: underline;
      }
      ::v-deep s,
      ::v-deep strike {
        text-decoration: line-through;
      }
      ::v-deep ul,
      ::v-deep ol {
        margin: 0;
        padding-left: 1.5em;
        text-align: left;
      }
      ::v-deep ul {
        list-style: disc;
      }
      ::v-deep ol {
        list-style: decimal;
      }
      ::v-deep li {
        list-style-position: outside;
      }
      &[placeholder]:empty::before {
        content: attr(placeholder);
        color: var(--light1);
      }
      &:focus {
        box-shadow: inset 0px 0px 0px 1px var(--light1);
      }
    }
  }

  .avatarLayout {
    position: absolute;
    top: 0;
    width: 40%;
    height: 100%;
    display: none;
    &.AVATAR_RIGHT {
      display: block;
    }
    &.AVATAR_LEFT {
      left: 0;
      display: block;
    }
    .avatarBackground {
      position: absolute;
      width: 100%;
      left: 0px;
      height: 100%;
      background-size: cover;
    }
  }

  .videoLayout {
    position: absolute;
    top: 0;
    height: 100%;
    display: none;
    &.ILLUSTRATED_LEFT {
      display: block;
      width: 40%;
      left: 0px;
    }
    &.ILLUSTRATED_RIGHT {
      display: block;
      width: 40%;
      right: 0px;
    }
    &.ILLUSTRATED_ONLY {
      display: block;
      width: 100%;
      left: 0px;
    }
  }

  // TEMPLATE AND THEME DEFINITION
  .coloredBackground {
    &.AVATAR_LEFT,
    &.ILLUSTRATED_LEFT {
      width: 60%;
      right: 0;
    }
    &.AVATAR_RIGHT,
    &.ILLUSTRATED_RIGHT {
      width: 60%;
      left: 0;
    }
  }

  .avatarLayout {
    &.AVATAR_LEFT {
      left: 0;
      .avatarBackground {
      }
    }
    &.AVATAR_RIGHT {
      right: 0;
      .avatarBackground {
      }
    }
  }
  .titleElement {
    &.ILLUSTRATED_ONLY {
      display: none;
    }
  }

  .descriptionElement {
    &.ILLUSTRATED_ONLY {
      display: none;
    }
  }
}
</style>
